// Client-side facet merging across eligible ducted systems (AHA-6681 CS-1):
// the compatible-combinations endpoint is strictly per-system, so the single
// filter section renders facets merged from every system's response. Filter
// `field` descriptors are backend-stable across systems, so merging by field
// is well-defined; every helper degenerates to identity for one system (the
// common case).
import {
  CombinationFilter,
  CombinationFilterOption
} from 'components/common/combination-filter-controls';
import {
  DuctedFuelToken,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';

export type SystemTypeFacetEntry = {
  code: string;
  label: string;
  count: number;
};

// Fixed Fuel display order + labels (AHA-6796): the ONE home for the fuel
// vocabulary shared by the Choose Systems chip row and the Existing
// Equipment want-picker fuel question — the two surfaces write the same
// filter state and must never drift on order or naming.
export const FUEL_CHIP_ORDER: Array<{ fuel: DuctedFuelToken; label: string }> =
  [
    { fuel: 'ng', label: 'Natural Gas' },
    { fuel: 'lp', label: 'Propane' },
    { fuel: 'o', label: 'Oil' },
    { fuel: 'e', label: 'Electric' }
  ];

// The fuels PRESENT in a code universe, in display order (AHA-6796 EE
// rows): the no-invisible-filter availability rule — a row renders only
// these, and the reducer's manual-preservation prune keys on the same
// derivation (universeFuels) so a kept manual selection is always
// clearable. Since the full-universe widening, the chip rows feed this the
// ductedUniverseCodes union (catalog-derived), not the recommended bound.
export function availableDuctedFuels(codes: string[]): DuctedFuelToken[] {
  const present = new Set(codes.map((code) => systemTypeCodeFuel(code)));
  return FUEL_CHIP_ORDER.map(({ fuel }) => fuel).filter((fuel) =>
    present.has(fuel)
  );
}

// Multi-select fuel toggle (AHA-6796): shared by the CS chip row and the
// EE row so the two gestures cannot diverge.
export function toggleFuelSelection(
  fuelSelections: DuctedFuelToken[],
  fuel: DuctedFuelToken
): DuctedFuelToken[] {
  return fuelSelections.includes(fuel)
    ? fuelSelections.filter((f) => f !== fuel)
    : [...fuelSelections, fuel];
}

// System-Type-chip prune on a fuel change (AHA-6796): the surviving type
// selections must classify to a still-selected fuel, or a stale chip could
// sit disjoint from the fuel-narrowed bound. An emptied fuel selection
// means ALL fuels — nothing to prune. Reference-stable when nothing drops
// (callers skip the dispatch then).
export function pruneTypeSelectionsToFuels<T extends { code: string }>(
  selections: T[],
  fuels: DuctedFuelToken[]
): T[] {
  if (fuels.length === 0) return selections;
  const pruned = selections.filter((selection) =>
    fuels.includes(systemTypeCodeFuel(selection.code))
  );
  return pruned.length === selections.length ? selections : pruned;
}

// Immutable enum-filter value toggle keyed by field (AHA-6796): the upsert
// the CS faceted spec controls always used, extracted so the EE chassis
// row writes the exact same shape (an emptied entry is removed, not left
// as an empty enum; the entry always re-appends last — the ui-slice
// manual-detection compares content, not order).
export function toggleEnumFilterValue(
  filters: CombinationFilter[],
  field: string,
  value: string
): CombinationFilter[] {
  const existing = filters.find((f) => f.field === field && f.kind === 'enum');
  const current = existing && existing.kind === 'enum' ? existing.values : [];
  const nextValues = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  const rest = filters.filter((f) => f.field !== field);
  return nextValues.length === 0
    ? rest
    : [...rest, { field, kind: 'enum', values: nextValues }];
}

// Merge rules per kind: range → min-of-mins / max-of-maxes (nulls ignored;
// all-null stays null); enum → values unioned with counts summed; boolean →
// counts summed. Descriptor order follows first appearance across the input
// lists (the backend registry order of the first system that has the field).
export function mergeCombinationFilterOptions(
  optionLists: CombinationFilterOption[][]
): CombinationFilterOption[] {
  const merged: CombinationFilterOption[] = [];
  const indexByField = new Map<string, number>();

  optionLists.forEach((options) => {
    options.forEach((option) => {
      const existingIndex = indexByField.get(option.field);
      if (existingIndex === undefined) {
        indexByField.set(option.field, merged.length);
        merged.push(
          option.kind === 'enum'
            ? { ...option, values: option.values.map((v) => ({ ...v })) }
            : { ...option }
        );
        return;
      }
      const existing = merged[existingIndex];
      if (existing.kind !== option.kind) return;
      if (existing.kind === 'range' && option.kind === 'range') {
        existing.min =
          existing.min == null || option.min == null
            ? existing.min ?? option.min
            : Math.min(existing.min, option.min);
        existing.max =
          existing.max == null || option.max == null
            ? existing.max ?? option.max
            : Math.max(existing.max, option.max);
      } else if (existing.kind === 'enum' && option.kind === 'enum') {
        option.values.forEach((incoming) => {
          const found = existing.values.find((v) => v.value === incoming.value);
          if (found) {
            found.count += incoming.count;
          } else {
            existing.values.push({ ...incoming });
          }
        });
      } else if (existing.kind === 'boolean' && option.kind === 'boolean') {
        existing.trueCount += option.trueCount;
        existing.falseCount += option.falseCount;
      }
    });
  });

  return merged;
}

// System-type facets unioned by code with counts summed, order of first
// appearance preserved.
export function mergeSystemTypeFacets(
  facetLists: SystemTypeFacetEntry[][]
): SystemTypeFacetEntry[] {
  const merged: SystemTypeFacetEntry[] = [];
  const indexByCode = new Map<string, number>();

  facetLists.forEach((facets) => {
    facets.forEach((facet) => {
      const existingIndex = indexByCode.get(facet.code);
      if (existingIndex === undefined) {
        indexByCode.set(facet.code, merged.length);
        merged.push({ ...facet });
      } else {
        merged[existingIndex].count += facet.count;
      }
    });
  });

  return merged;
}

export function sumTotalCounts(totalCounts: number[]): number {
  return totalCounts.reduce((sum, count) => sum + count, 0);
}

export function sumFacetCounts(facets: Array<{ count: number }>): number {
  return facets.reduce((sum, facet) => sum + facet.count, 0);
}

// FETCH-level fuel narrowing (AHA-6796 Fuel chips): the Fuel selection
// narrows a system's recommended bound before the chip resolution. Mirror
// of the resolveDuctedEffectiveCodes collapse convention — a fuel selection
// matching NOTHING in this system's bound collapses to the full bound
// (never widens past it, never sends [] = unbounded); strict zero-row
// honesty is the display layer's job (filterSystemTypesByFuel). Empty
// fuels = all fuels. Reference-stable: every branch returns the input
// array unchanged when nothing was filtered out.
export function narrowCodesByFuel(
  codes: string[],
  fuels: DuctedFuelToken[]
): string[] {
  if (fuels.length === 0) return codes;
  const selected = new Set(fuels);
  const survivors = codes.filter((code) =>
    selected.has(systemTypeCodeFuel(code))
  );
  if (survivors.length === 0) return codes;
  return survivors.length === codes.length ? codes : survivors;
}

// STRICT display-side fuel filter (AHA-6796 Fuel chips): unlike the fetch
// narrowing above this never collapses — a fuel selection matching nothing
// filters to zero entries (the rail's honest empty state), so a collapse-
// widened fetch never leaks wrong-fuel rows. Empty fuels = all fuels.
export function filterSystemTypesByFuel<T extends { code: string }>(
  systemTypes: T[],
  fuels: DuctedFuelToken[]
): T[] {
  if (fuels.length === 0) return systemTypes;
  const selected = new Set(fuels);
  return systemTypes.filter((st) => selected.has(systemTypeCodeFuel(st.code)));
}

// Recommended bound for one system's Choose Systems contribution (AHA-6681
// requirement D, wants-gateway in sub-build 3), read off its LES-4 summary
// record (structure.compatibleResultsBySystem): the checks always run
// against the recommended codes now, so each entry's stored rows ARE the
// recommended-set facet, and the bound is the first-appearance-ordered
// UNION across the alternatives then kept entries (both already
// recommended-filtered at store time; neither side is guaranteed a superset
// of the other, so the union is the honest bound). Null = no record exists
// (fresh hydration / old rows): no bound is resolvable, so callers must not
// fetch (they derive a summary first, or skip). An empty array is
// meaningful: a zero-result summary bounds the system to NOTHING (callers
// skip its fetch entirely).
export function boundedCodesFor(
  record:
    | {
        kept?: { systemTypes: Array<{ code: string }> };
        alternatives?: { systemTypes: Array<{ code: string }> };
      }
    | undefined
): string[] | null {
  if (!record) return null;
  const codes: string[] = [];
  const seen = new Set<string>();
  [record.alternatives, record.kept].forEach((entry) => {
    entry?.systemTypes.forEach((st) => {
      if (!seen.has(st.code)) {
        seen.add(st.code);
        codes.push(st.code);
      }
    });
  });
  return codes;
}

// The FULL eligible ducted universe for the fuel chip vocabulary (AHA-6796
// fuel chips = full universe): first-appearance-ordered dedup union across
// the eligible systems of (catalog codes when present-and-non-empty, else
// the recommended bound, else []). The per-system catalog
// (structure.recommendationCatalogCodesBySystem — the deduped
// context.systemTypes code union, the same universe the "Show all (N)"
// want-tile list draws from) is the honest full universe; the bound
// fallback covers a system whose catalog hasn't landed yet (in-session adds
// before the strip's context fetch settles) — the vocabulary is then
// temporarily today's bound-derived one, never wider than the truth.
// This is the ONE derivation shared by the context-reset dispatch site and
// buildDefaultDuctedFetchParams, so the reset's universeFuels payload and
// the pre-warm builder's recomputation are byte-identical by construction
// (requestBodyHash parity).
export function ductedUniverseCodes(
  eligibleSystems: Array<{ uniqueId: string }>,
  catalogCodesBySystem: Record<string, string[] | undefined>,
  compatibleResultsBySystem: Record<
    string,
    | {
        kept?: { systemTypes: Array<{ code: string }> };
        alternatives?: { systemTypes: Array<{ code: string }> };
      }
    | undefined
  >
): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();
  eligibleSystems.forEach((system) => {
    const catalog = catalogCodesBySystem[system.uniqueId];
    const perSystem =
      catalog && catalog.length > 0
        ? catalog
        : boundedCodesFor(compatibleResultsBySystem[system.uniqueId]) ?? [];
    perSystem.forEach((code) => {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    });
  });
  return codes;
}

// Priority ordering for the ducted rail's default panel (AHA-6681): the
// LES-4 summary bound carries the recommendation PRIORITY order (the checks
// send priority-ordered codes and the store keeps that order), while the
// slice facet arrives in server order — so the representative cards reorder
// to the bound. Unknown codes append after the ranked ones in their original
// relative order (safe fallback per spec — never dropped, never a crash);
// an empty orderedCodes is the identity. Inputs are never mutated.
export function orderSystemTypesByCodes(
  systemTypes: SystemTypeFacetEntry[],
  orderedCodes: string[]
): SystemTypeFacetEntry[] {
  if (orderedCodes.length === 0) return systemTypes;
  const ranked = orderedCodes.flatMap((code) =>
    systemTypes.filter((st) => st.code === code)
  );
  const rankedSet = new Set(orderedCodes);
  return [...ranked, ...systemTypes.filter((st) => !rankedSet.has(st.code))];
}

// One result set's slice of the System Type chip selection (AHA-6681
// kept/alternatives filter split). `active` is GLOBAL — any selection on
// either set activates strict scoping everywhere — while `codes` carries
// only this set's picks: while active, a set whose scoped codes are empty
// shows/fetches ZERO rows (per-set "empty means all" would leave the other
// set showing plain rows of the selected type).
export type ScopedDuctedTypeSelection = {
  active: boolean;
  codes: string[];
};

export function scopedDuctedTypeCodes(
  selections: Array<{ code: string; resultSet: 'kept' | 'alternatives' }>,
  resultSet: 'kept' | 'alternatives'
): ScopedDuctedTypeSelection {
  return {
    active: selections.length > 0,
    codes: selections
      .filter((selection) => selection.resultSet === resultSet)
      .map((selection) => selection.code)
  };
}

// Fetch-side variant of the scoped selection (requirement D): a system's
// representative fetch must stay within ITS recommended bound even with no
// selection active (the facet can carry non-recommended types), so the
// result is always an active selection — the full bound when nothing is
// selected, the scoped codes intersected with the bound otherwise (a chip
// from another system's bound never leaks a non-recommended rep here).
export function scopedTypeCodesWithinBound(
  selections: Array<{ code: string; resultSet: 'kept' | 'alternatives' }>,
  resultSet: 'kept' | 'alternatives',
  boundedCodes: string[]
): ScopedDuctedTypeSelection {
  const scoped = scopedDuctedTypeCodes(selections, resultSet);
  if (!scoped.active) return { active: true, codes: boundedCodes };
  const bounded = new Set(boundedCodes);
  return { active: true, codes: scoped.codes.filter((c) => bounded.has(c)) };
}

// Display type list for the ducted Systems rail (AHA-6681 CS-2). The server
// recomputes the systemTypes facet over the spec-filtered set but does NOT
// apply the type selection to it (non-exclude-self), and returns all known
// types with count 0 when excluded — so keep count > 0 and intersect with
// the scoped selection client-side. Inactive → all count>0 types; active →
// intersect with the scoped codes (possibly empty → none).
export function displayableSystemTypes(
  systemTypes: SystemTypeFacetEntry[],
  scoped: ScopedDuctedTypeSelection
): SystemTypeFacetEntry[] {
  const withRows = systemTypes.filter((st) => st.count > 0);
  return scoped.active
    ? withRows.filter((st) => scoped.codes.includes(st.code))
    : withRows;
}
